import { DestroyRef, Inject, Injectable } from '@angular/core';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthGuardService } from './authentication-guard.service';
import { SHARED_LIBRARY_CONFIG } from 'projects/petszel-shared/src/public-api';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { Store } from "@ngrx/store";
import { AppStateModel } from "../shared-store/app-state.model";
import { selectAnimalId } from "../shared-store/selected-variables/selected-variables.selectors";
import { selectPets } from "../shared-store/pet/pet.selectors";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

type AnalyticsParams = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private environmentType!: string;
  private ownerId!: string;
  private ownerIdSubject = new BehaviorSubject<string | null>(null);
  private pageEnterTime: number | null = null;

  private landingOverridePath: string | null =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('landing_override_path')
      : null;

  private hasSentFirstView = false;

  constructor(
    @Inject(SHARED_LIBRARY_CONFIG) private config: any,
    private router: Router,
    private authService: AuthGuardService,
    private logger: NGXLogger,
    private store: Store<AppStateModel>,
    private destroyRef: DestroyRef
  ) {
    void this.authService;

    this.environmentType = this.config.environmentType;

    // Initialize dataLayer
    this.ensureDataLayer();

    // - tracks time spent on previous page
    // - starts timer for current page
    // - sends virtual page_view (except magic-link routes)
    this.router.events
      .pipe(
        filter(
          (event: RouterEvent): event is NavigationEnd =>
            event instanceof NavigationEnd
        )
      )
      .subscribe((nav: NavigationEnd) => {
        this.logger.log(
          '[analytics] NavigationEnd',
          nav.url,
          nav.urlAfterRedirects
        );

        // Track time spent on previous page
        this.trackPageTime();

        // Start timing the new page
        this.pageEnterTime = performance.now();
        this.logger.log('[analytics] New page entry time set:', this.pageEnterTime);

        const path = this.landingOverridePath ?? nav.urlAfterRedirects;
        const isMagic = /^\/passwordless(\/|$)/i.test(path);

        if (!isMagic) {
          this.sendVirtualPageView(path);

          if (!this.hasSentFirstView) {
            this.hasSentFirstView = true;
          }

          if (this.landingOverridePath) {
            this.landingOverridePath = null;
            sessionStorage.removeItem('landing_override_path');
          }
        }
      });

    // When ownerId becomes available, push identity/user context to GTM
    this.ownerIdSubject.subscribe((ownerId) => {
      if (!ownerId) return;

      const trafficType =
        this.environmentType === 'local' ? 'internal' : 'external';

      this.logger.log(`[analytics] ownerId set. Traffic type: ${trafficType}`);

      this.pushToDataLayer({
        event: 'analytics_user_context',
        user_id: ownerId,
        page_path: this.router.url,
        traffic_type: trafficType,
      });

      // local/internal traffic signal behavior
      if (this.environmentType === 'local') {
        this.pushToDataLayer({
          event: 'internal_traffic',
          internal_traffic: 'yes',
          user_id: ownerId,
        });
      }
    });
  }

  // ----------------------------
  // Core GTM / dataLayer helpers
  // ----------------------------

  private ensureDataLayer(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
  }

  private pushToDataLayer(payload: Record<string, any>): void {
    if (typeof window === 'undefined') return;
    this.ensureDataLayer();

    window.dataLayer.push(payload);

    this.logger.log('[analytics] dataLayer.push', payload);
  }

  private getTrafficType(): 'internal' | 'external' {
    return this.environmentType === 'local' ? 'internal' : 'external';
  }

  private getBaseContext(): AnalyticsParams {
    return {
      user_id: this.ownerId || undefined,
      traffic_type: this.getTrafficType(),
      environment_type: this.environmentType,
      page_path: this.router.url || undefined,
    };
  }

  // ----------------------------
  // Pageview / timing
  // ----------------------------

  private sendVirtualPageView(path: string): void {
    const ownerId = this.getOwnerId();

    combineLatest({
      petId: this.store.select(selectAnimalId),
      pets: this.store.select(selectPets)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      const pet = data.pets?.find(pet => pet.id === data.petId);

      // Custom event name for GTM trigger
      this.pushToDataLayer({
        event: 'virtual_page_view',
        page_location: `${location.origin}${path}`,
        page_path: path,
        page_title: document.title,
        user_id: ownerId || undefined,
        traffic_type: this.getTrafficType(),
        environment_type: this.environmentType,
        has_sent_first_view: this.hasSentFirstView,
        pet_id: data.petId || '',
        shelter_account_id: pet?.shelterAccountId || '',
        pet_species: pet?.animalType || '',
      });
    });
  }

  trackPageTime(): void {
    if (this.pageEnterTime && this.getOwnerId()) {
      const timeSpent = (performance.now() - this.pageEnterTime) / 1000;

      this.trackEvent('time_spent', {
        event_category: 'Engagement',
        event_label: 'Time Spent',
        value: timeSpent,
        page_path: this.router.url,
        user_id: this.getOwnerId(),
      });

      this.pageEnterTime = null;
    } else {
    }
  }

  // ----------------------------
  // Identity / user props
  // ----------------------------

  setOwnerId(id: string): void {
    this.ownerId = id;
    this.ownerIdSubject.next(id);
  }

  getOwnerId(): string {
    return this.ownerId;
  }

  private setGAUserId(ownerId: string): void {
    // GTM-first replacement for gtag('set', 'user_properties', { user_id })
    this.pushToDataLayer({
      event: 'set_user_id',
      user_id: ownerId,
    });
  }

  setUserProperties(properties: { [key: string]: any }): void {
    // GTM-first replacement for gtag('set', 'user_properties', ...)
    this.pushToDataLayer({
      event: 'set_user_properties',
      user_id: this.ownerId || undefined,
      user_properties: properties,
    });
  }

  // ----------------------------
  // Generic event tracking
  // ----------------------------

  trackEvent(eventName: string, params: { [key: string]: any }): void {
    if (this.ownerId) {
      const userProperties: { [key: string]: any } = {};

      if (eventName === 'animal_type_interaction') {
        userProperties['animal_type'] = params['event_label'];
      } else if (eventName === 'business_details') {
        userProperties['business_category'] = params['event_category'];
        userProperties['account_id'] = params['account_id'];
        userProperties['legal_name'] = params['legal_name'];
      } else if (eventName === 'set_user_gender') {
        userProperties['gender'] = params['user_gender'];
      } else if (eventName === 'page_view') {
        userProperties['pagePath'] = params['page_path'];
      } else if (eventName === 'article_view') {
        userProperties['articleTitle'] = params['event_label'];
        userProperties['articleId'] = params['article_id'];
      } else if (eventName === 'search') {
        userProperties['searchQuery'] = params['event_label'];
      } else if (eventName === 'button_click') {
        userProperties['button_label'] = params['event_label'];
      }

      this.setUserProperties(userProperties);

      // Push event for GTM.
      this.pushToDataLayer({
        event: eventName,
        ...this.getBaseContext(),
        ...params,
        user_id: this.ownerId,
      });
    } else {
    }
  }

  buildUserProperties(
    eventName: string,
    params: { [key: string]: any }
  ): { [key: string]: any } | null {
    const userProperties: { [key: string]: any } = {};

    // Depending on the event, set different user properties
    switch (eventName) {
      case 'animal_type_interaction':
        userProperties['animal_type'] = params['event_label'];
        break;
      case 'business_details':
        userProperties['business_category'] = params['event_category'];
        userProperties['account_id'] = params['account_id'];
        userProperties['legal_name'] = params['legal_name'];
        break;
      case 'set_user_gender':
        userProperties['gender'] = params['user_gender'];
        break;
      case 'page_view':
        userProperties['pagePath'] = params['page_path'];
        break;
      case 'article_view':
        userProperties['articleTitle'] = params['event_label'];
        userProperties['articleId'] = params['article_id'];
        break;
      case 'search':
        userProperties['searchQuery'] = params['event_label'];
        break;
      // Add cases for other event types as needed
      default:
        // If no user properties are to be set for this event, return null
        return null;
    }

    // Return the user properties object if properties are set
    return userProperties;
  }

  // ----------------------------
  // Specific event wrappers
  // ----------------------------

  trackAnimalType(animalType: string, ownerId: string): void {
    this.trackEvent('animal_type', {
      event_category: 'Animal Type',
      event_label: animalType,
      animal_type: animalType,
      owner_id: ownerId,
    });
  }

  trackSurveySubmission(
    type: 'start' | 'end',
    surveyId: string,
    ownerId: string,
    petId: string
  ): void {
    const event: string = type === 'start' ? 'survey_start' : 'survey_complete';

    this.trackEvent(event, {
      event_category: 'Survey',
      event_label: type === 'start' ? 'Survey Start' : 'Survey Complete',
      survey_id: surveyId,
      owner_id: ownerId,
      pet_id: petId,
    });
  }

  trackBusinessDetails(accountId: string, legalName: string): void {
    this.trackEvent('business_details', {
      event_category: 'Business',
      event_label: 'Business Details',
      account_id: accountId,
      legal_name: legalName,
    });
  }

  trackUserGender(gender: string): void {
    this.trackEvent('set_user_gender', {
      event_category: 'User Info',
      event_label: 'Gender',
      user_gender: gender,
    });
  }

  trackArticleView(articleId: string, articleTitle: string): void {
    this.logger.log(
      'trackArticleView called with articleId:',
      articleId,
      'and articleTitle:',
      articleTitle
    );

    const ownerId = this.getOwnerId();
    const pagePath = this.router.url; // Get the current router path
    this.logger.log(pagePath);

    if (ownerId) {
      this.trackEvent('article_view', {
        event_category: 'Article',
        event_label: articleTitle,
        article_id: articleId,
        user_id: ownerId,
        page_path: pagePath,
      });
    } else {
      this.logger.error(`User ID not set for article view event`);
    }
  }

  async getCurrentPagePath(): Promise<string> {
    return new Promise<string>((resolve) => {
      this.router.events
        .pipe(
          filter(
            (event: RouterEvent): event is NavigationEnd =>
              event instanceof NavigationEnd
          )
        )
        .subscribe((event: NavigationEnd) => {
          resolve(event.urlAfterRedirects);
        });
    });
  }

  trackUserProvidersSearch(data: any): void {
    this.trackEvent('providers_search', {
      ...data,
    });
  }

  trackUserProviderResultCardClicked(data: any): void {
    this.trackEvent('providers_result_card_clicked', {
      ...data,
    });
  }

  trackFormSubmission(formName: string, action: string): void {
    const ownerId = this.getOwnerId();

    // Prepare the event parameters
    const eventParams = {
      event_category: 'Providers Form',
      event_label: formName,
      user_id: ownerId,
    };

    // Check if the ownerId is available
    if (ownerId) {
      // Log the event for debugging
      this.logger.log(`Tracking Event: ${action}`, eventParams);

      // GTM-first replacement for gtag('event', action, eventParams)
      this.pushToDataLayer({
        event: action,
        ...this.getBaseContext(),
        ...eventParams,
      });
    } else {
      // Log an error for missing user ID
      this.logger.error(
        `User ID not set for form submission event: ${formName}`
      );
    }
  }

  trackProviderCardClick(place: any, index: number | null, listName: string): void {
    const items = [
      {
        item_id:
          place.placeId || place.googlePlaceId || place.id || '(unknown)',
        item_name: place.displayName || place.name || '(unknown)',
        item_category:
          place.primaryType ||
          place.primaryCategory ||
          (place.badges?.[0] ?? undefined),
        index: typeof index === 'number' ? index : undefined,
        item_list_name: listName,
        affiliation: place.provider?.bookNowLink ? 'Online Booking' : undefined,
        location_id:
          place.location?.lat && place.location?.lng
            ? `${place.location.lat},${place.location.lng}`
            : undefined,
      },
    ];

    // Preserve GA4 ecommerce-style select_item event semantics
    this.pushToDataLayer({
      event: 'select_item',
      ...this.getBaseContext(),
      item_list_name: listName,
      items,
    });

    this.trackUserProviderResultCardClicked({
      partner_id: place.placeId || place.googlePlaceId || place.id,
      display_name: place.displayName || place.name,
      category: place.primaryType || place.primaryCategory,
      distance_meters: place.distance,
      has_online_booking: !!place.provider?.bookNowLink,
      list_name: listName,
      list_index: index,
    });
  }

  // ----------------------------
  // Banner tracking
  // ----------------------------

  trackBannerView(bannerData: {
    shelter_id: string;
    banner_content_name: string;
    banner_placement: string;
    days_since_adoption: number;
    banner_tags: string;
    species: string;
    lifestage: string;
    brand_partner: string;
  }): void {
    const ownerId = this.getOwnerId();

    if (!ownerId) {
      this.logger.error('User ID not set for banner view event');
      return;
    }

    // GA4 view_promotion event (via GTM)
    this.pushToDataLayer({
      event: 'view_promotion',
      ...this.getBaseContext(),
      debug_mode: true,
      items: [
        {
          promotion_id: bannerData.banner_content_name,
          promotion_name: bannerData.banner_content_name,
          creative_slot: bannerData.banner_placement,
        },
      ],
      shelter_id: bannerData.shelter_id,
      days_since_adoption: bannerData.days_since_adoption,
      species: bannerData.species,
      lifestage: bannerData.lifestage,
      brand_partner: bannerData.brand_partner,
      banner_tags: bannerData.banner_tags,
      user_id: ownerId,
    });

    this.logger.log('Banner view tracked', bannerData);
  }

  trackBannerClick(bannerData: {
    shelter_id: string;
    banner_content_name: string;
    banner_placement: string;
    days_since_adoption: number;
    banner_tags: string;
    species: string;
    lifestage: string;
    brand_partner: string;
    banner_url: string;
  }): void {
    const ownerId = this.getOwnerId();

    if (!ownerId) {
      this.logger.error('User ID not set for banner click event');
      return;
    }

    // GA4 select_promotion event (via GTM)
    this.pushToDataLayer({
      event: 'select_promotion',
      ...this.getBaseContext(),
      debug_mode: true,
      transport_type: 'beacon',
      items: [
        {
          promotion_id: bannerData.banner_content_name,
          promotion_name: bannerData.banner_content_name,
          creative_slot: bannerData.banner_placement,
        },
      ],
      shelter_id: bannerData.shelter_id,
      days_since_adoption: bannerData.days_since_adoption,
      species: bannerData.species,
      lifestage: bannerData.lifestage,
      brand_partner: bannerData.brand_partner,
      banner_tags: bannerData.banner_tags,
      user_id: ownerId,
    });

    // Custom event for additional tracking
    this.pushToDataLayer({
      event: 'banner_click',
      ...this.getBaseContext(),
      debug_mode: true,
      transport_type: 'beacon',
      event_category: 'engagement',
      event_label: bannerData.banner_content_name,
      value: 1,
      banner_url: bannerData.banner_url,
      banner_placement: bannerData.banner_placement,
      shelter_id: bannerData.shelter_id,
      days_since_adoption: bannerData.days_since_adoption,
      species: bannerData.species,
      lifestage: bannerData.lifestage,
      brand_partner: bannerData.brand_partner,
      banner_tags: bannerData.banner_tags,
      user_id: ownerId,
    });

    this.logger.log('Banner click tracked', bannerData);
  }
}
